import {
  logToLambda,
  sendLogOnLeave,
  createAuthLog,
  isLambdaLoggingEnabled,
  warmUpClientIp,
} from "./lambdaLogger";
import {
  getCookie,
  setCookie,
  getSharedCookieDomain,
  deleteCookieAllDomains,
} from "../Core/utils/cookieUtils";
import { decodeToken } from "../Core/utils/decodeToken";
import { decrypt } from "../Core/utils/others";

/**
 * Sign-up / sign-in / logout logging, sent to the logging Lambda (MongoDB)
 * through lambdaLogger. StudentPortal has a copy of this file with the same
 * event names and fields, so one query covers both apps.
 *
 * Components use the useAuthEventLog hook; code outside React (the API client,
 * sessionExpired) calls logAuthEvent directly. Both send the same entry.
 *
 * Every entry: event_name, event_env, event_date_ist, ipAddress, app, mode,
 * studentId, role, plus the fields the call site passes. Step events carry
 * status "success" | "failed" (failures add httpStatus, responseMsg and
 * requestId). Never OTPs, passwords or tokens.
 *
 * Nothing is logged for an impersonated session (staff viewing as a student):
 * it is capped at 15 minutes and never refreshed, so its expiry would count
 * as a student logout.
 */

const APP = "sandbox";

export const AUTH_EVENTS = Object.freeze({
  IDENTIFIER_SUBMITTED: "AUTH_IDENTIFIER_SUBMITTED",
  OTP_SENT: "AUTH_OTP_SENT",
  OTP_RESENT: "AUTH_OTP_RESENT",
  OTP_VERIFIED: "AUTH_OTP_VERIFIED",
  PASSWORD_LOGIN: "AUTH_PASSWORD_LOGIN",
  PASSWORD_SET: "AUTH_PASSWORD_SET",
  GOOGLE_LOGIN: "AUTH_GOOGLE_LOGIN",
  CHILD_SELECTED: "AUTH_CHILD_SELECTED",
  ONBOARDING_SUBMITTED: "AUTH_ONBOARDING_SUBMITTED",
  SIGNUP: "AUTH_SIGNUP",
  SIGNIN: "AUTH_SIGNIN",
  PASSWORD_RESET_REQUESTED: "AUTH_PASSWORD_RESET_REQUESTED",
  PASSWORD_RESET_OTP_VERIFIED: "AUTH_PASSWORD_RESET_OTP_VERIFIED",
  PASSWORD_RESET_COMPLETED: "AUTH_PASSWORD_RESET_COMPLETED",
  LOGOUT: "AUTH_LOGOUT",
  SESSION_EXPIRED: "AUTH_SESSION_EXPIRED",
  PROFILE_SWITCH: "AUTH_PROFILE_SWITCH",
  // Stale-tab recovery kept the student signed in (see
  // Services/recoveryDiagnostics.js).
  SESSION_RECOVERED: "AUTH_SESSION_RECOVERED",
});

// Why sessionExpired() ended a session — one per call site in createApiClient
// and authSyncListener.
export const LOGOUT_REASONS = Object.freeze({
  LEGACY_TOKEN: "legacy-token",
  // Another tab found the shared logout signal (cy_logged_out): the student
  // was logged out in a Sandbox or a Student Portal tab. Call sites pass
  // LOGGED_OUT_IN_OTHER_TAB; logAuthEvent narrows it to the app the logout
  // happened in when the logout-origin cookie says (see LOGOUT_ORIGIN_COOKIE).
  LOGGED_OUT_IN_OTHER_TAB: "logged-out-in-other-tab",
  LOGGED_OUT_IN_SANDBOX: "logged-out-in-sandbox",
  LOGGED_OUT_IN_STUDENTPORTAL: "logged-out-in-studentportal",
  NO_LOCAL_SESSION: "no-local-session",
  REFRESH_FAILED: "refresh-failed",
  UNAUTHORIZED_AFTER_REFRESH: "401-after-refresh",
});

// Events that start a session (a new sessionId + loginAt) and events that end
// one (logged with how long it lasted).
const SESSION_START_EVENTS = [
  AUTH_EVENTS.SIGNIN,
  AUTH_EVENTS.SIGNUP,
  AUTH_EVENTS.PROFILE_SWITCH,
];
const SESSION_END_EVENTS = [AUTH_EVENTS.LOGOUT, AUTH_EVENTS.SESSION_EXPIRED];

// The session marker lives in a cookie on the shared domain, not localStorage:
// a student often signs in on one app and is logged out on the other, and the
// two apps only share cookies.
export const AUTH_SESSION_COOKIE = "cy_auth_session";
// Where the last logout happened (app, event, button or reason), on the same
// shared domain, so a tab that picks up the logout signal later can log which
// app logged the student out. Cleared by the next sign-in.
export const LOGOUT_ORIGIN_COOKIE = "cy_auth_logout";
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

// When a logout was last logged, on window rather than module scope: in
// embedded mode Sandbox and the portal run in one page, so whichever app logs
// a logout first stops the other logging the same one. Time-limited, so a
// later logout in a page that never reloaded (hash-only routes) still logs.
const LOGOUT_LOGGED_AT = "__cyAuthLogoutLoggedAt";
const SAME_LOGOUT_WINDOW_MS = 10000;
// Set once a logout/switch has started leaving the page (see
// logAuthEventBeforeLeaving and sessionExpired).
const LEAVING_FLAG = "__cyAuthLeaving";

const newSessionId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const startAuthSession = (studentId) => {
  const session = {
    sessionId: newSessionId(),
    loginAt: new Date().toISOString(),
    studentId: studentId || null,
  };
  try {
    setCookie(AUTH_SESSION_COOKIE, JSON.stringify(session), {
      domain: getSharedCookieDomain(),
      maxAge: ONE_YEAR_SECONDS,
    });
  } catch (_) {
    /* the log still goes out, just without a session to time */
  }
  return session;
};

const OTHER_TAB_REASONS = [
  LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB,
  LOGOUT_REASONS.LOGGED_OUT_IN_SANDBOX,
  LOGOUT_REASONS.LOGGED_OUT_IN_STUDENTPORTAL,
];
const REASON_BY_ORIGIN_APP = {
  sandbox: LOGOUT_REASONS.LOGGED_OUT_IN_SANDBOX,
  portal: LOGOUT_REASONS.LOGGED_OUT_IN_STUDENTPORTAL,
};

// Remembers a logout that started in this tab, for the tabs that follow it.
const recordLogoutOrigin = (app, eventName, details) => {
  try {
    setCookie(
      LOGOUT_ORIGIN_COOKIE,
      JSON.stringify({
        app,
        event: eventName,
        source: details.source || null,
        reason: details.reason || null,
        at: new Date().toISOString(),
      }),
      { domain: getSharedCookieDomain(), maxAge: ONE_YEAR_SECONDS }
    );
  } catch (_) {
    /* the other tab falls back to logged-out-in-other-tab */
  }
};

// For a session ended because another tab logged out: the app it happened in,
// and how (event plus button or reason). Unknown origin keeps the fallback.
const otherTabLogoutDetails = () => {
  try {
    const raw = getCookie(LOGOUT_ORIGIN_COOKIE);
    const origin = raw ? JSON.parse(raw) : null;
    const reason = REASON_BY_ORIGIN_APP[origin?.app];
    if (!reason) return { reason: LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB };
    return {
      reason,
      originApp: origin.app,
      originEvent: origin.event || null,
      originSource: origin.source || null,
      originReason: origin.reason || null,
      originAt: origin.at || null,
    };
  } catch (_) {
    return { reason: LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB };
  }
};

export const readAuthSession = () => {
  try {
    const raw = getCookie(AUTH_SESSION_COOKIE);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

// Timing of the shared session — only when it belongs to the student being
// logged out. The cookie outlives local storage, so a stale session (another
// student's, or one from days ago) must not borrow it.
const sessionTiming = (studentId) => {
  const session = readAuthSession();
  if (session?.studentId && studentId && session.studentId !== studentId) {
    return { sessionId: null, loginAt: null, sessionDurationSec: null };
  }
  if (!session?.loginAt) {
    return { sessionId: session?.sessionId || null, loginAt: null, sessionDurationSec: null };
  }
  const elapsedMs = Date.now() - Date.parse(session.loginAt);
  return {
    sessionId: session.sessionId || null,
    loginAt: session.loginAt,
    sessionDurationSec: Number.isFinite(elapsedMs)
      ? Math.max(0, Math.round(elapsedMs / 1000))
      : null,
  };
};

const idOf = (user) => user.userId || user.studentId || user.user_id || null;

const currentUser = () => {
  try {
    const user = decrypt(localStorage.getItem("sandbox_user")) || {};
    // Embedded in the portal, sandbox_user can lack the id; the portal's own
    // `user` key has it.
    const portalUser = idOf(user) ? {} : decrypt(localStorage.getItem("user")) || {};
    const token =
      user.ACCESS_TOKEN || user.Authorization || portalUser.ACCESS_TOKEN || portalUser.Authorization;
    return {
      studentId: idOf(user) || idOf(portalUser),
      role: user.role || portalUser.role || null,
      impersonated: isImpersonatedToken(token),
    };
  } catch (_) {
    return { studentId: null, role: null, impersonated: false };
  }
};

// cy-sso marks an impersonation token with impersonatedBy (the staff member).
const isImpersonatedToken = (token) => !!decodeToken(token).impersonatedBy;

/** The signed-in student's id, or null — e.g. the "from" side of a profile switch. */
export const getCurrentStudentId = () => currentUser().studentId;

/** HTTP status, backend message and request id of a failed auth call. */
export const authErrorDetails = (err) => ({
  httpStatus: err?.response?.status ?? null,
  responseMsg:
    err?.response?.data?.msg ||
    err?.response?.data?.message ||
    (err?.response ? null : err?.message) ||
    null,
  requestId: err?.response?.headers?.["x-request-id"] || null,
});

/**
 * What cy-sso answered to a refresh that ended the session: "rejected" (401,
 * refresh token no longer valid), "server-error" (5xx), "invalid" (200 with
 * valid:false — the session was deleted server-side), or "other".
 */
export const refreshFailureDetails = (err) => {
  const status = err?.response?.status;
  let refreshResult = "other";
  if (status === 401) refreshResult = "rejected";
  else if (status >= 500) refreshResult = "server-error";
  else if (err?.refreshAuthFailed) refreshResult = "invalid";
  return { refreshResult, ...authErrorDetails(err) };
};

/**
 * Builds the entry synchronously — so a caller can clear the session right
 * after — and sends it in the background. Resolves when sent; never rejects.
 */
export const logAuthEvent = (eventName, details = {}) =>
  sendAuthEvent(eventName, details, logToLambda);

const sendAuthEvent = (eventName, details, send) => {
  try {
    const user = currentUser();
    if (user.impersonated) return Promise.resolve();

    const endedByOtherTab =
      eventName === AUTH_EVENTS.SESSION_EXPIRED && OTHER_TAB_REASONS.includes(details.reason);
    if (endedByOtherTab) details = { ...details, ...otherTabLogoutDetails() };
    if (SESSION_END_EVENTS.includes(eventName)) {
      const lastAt = window[LOGOUT_LOGGED_AT] || 0;
      if (Date.now() - lastAt < SAME_LOGOUT_WINDOW_MS) return Promise.resolve();
      window[LOGOUT_LOGGED_AT] = Date.now();
      // A logout that starts here; one caused by another tab keeps that tab's.
      if (!endedByOtherTab) recordLogoutOrigin(APP, eventName, details);
    }

    let session = {};
    if (SESSION_START_EVENTS.includes(eventName) && details.status !== "failed") {
      // A new session: its own logout must be logged and handled.
      window[LOGOUT_LOGGED_AT] = 0;
      window[LEAVING_FLAG] = false;
      try {
        deleteCookieAllDomains(LOGOUT_ORIGIN_COOKIE);
      } catch (_) {
        /* never block the sign-in log */
      }
      const started = startAuthSession(details.studentId || user.studentId);
      session = { sessionId: started.sessionId, loginAt: started.loginAt };
    } else if (SESSION_END_EVENTS.includes(eventName)) {
      session = sessionTiming(details.studentId || user.studentId);
    }

    const entry = createAuthLog({
      eventName,
      app: APP,
      mode: window.__SANDBOX_IS_PLUGIN__ ? "embedded" : "standalone",
      role: user.role,
      ...session,
      ...details,
      // A caller's missing id (e.g. a login response without userId) must not
      // erase the one already known from the stored session.
      studentId: details.studentId || user.studentId,
    });
    return Promise.resolve(send(entry)).catch(() => {});
  } catch (_) {
    return Promise.resolve();
  }
};

/**
 * For a log sent just before the page navigates away (logout, profile switch):
 * sent as a keepalive request the browser finishes after the page has gone,
 * so the caller never waits on the logger: navigate right after the call.
 */
export const logAuthEventBeforeLeaving = (eventName, details = {}) => {
  // The page is on its way out (logout redirect, switch reload). Marked so a
  // request that 401s meanwhile doesn't run sessionExpired() — which would
  // show "Session expired" after a voluntary logout.
  window[LEAVING_FLAG] = true;
  sendAuthEvent(eventName, details, sendLogOnLeave);
};

/**
 * True once a logout/switch has started leaving the page. sessionExpired() pairs
 * it with "no session stored", so it only ever suppresses the toast for a
 * logout already in progress — never a session that exists.
 */
export const isLeavingPage = () => window[LEAVING_FLAG] === true;

// Look the IP up early for a signed-in visitor, so a logout log (which never
// waits for it) already has it — at load, or when a sign-in in another tab
// fills the session in. A sign-in in this tab looks it up with its own log.
// Skipped when logging is off.
try {
  if (isLambdaLoggingEnabled()) {
    if (localStorage.getItem("sandbox_user")) warmUpClientIp();
    window.addEventListener("storage", (e) => {
      if (e.key === "sandbox_user" && e.newValue) warmUpClientIp();
    });
  }
} catch (_) {
  /* never block module load */
}
